import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { ChartComponentProps, getBaseOption, formatTooltipValue, formatNumberShort, ECHARTS_OPTS, CHART_GRID_LEFT, isNumericLike, toNumericValue } from './chartConfig';

const FALLBACK_HEAT = ['#FFF3D6', '#F0DEAA', '#E8B84F', '#C97A3F', '#A63D40', '#2C5F7C'];

type HeatPoint = {
  xIdx: number;
  yIdx: number;
  value: number;
  raw?: Record<string, any> | null;
};

type HeatModel = {
  xCategories: string[];
  yCategories: string[];
  points: HeatPoint[];
  min: number;
  max: number;
  primaryLabel: string;
  secondaryLabel?: string;
  // Axis titles — the row/col dimension display names (e.g. "End
  // Customer" / "Prod Class"). Distinct from primaryLabel, which is the
  // measure shown in the colour scale + tooltip.
  xAxisLabel?: string;
  yAxisLabel?: string;
};

// Generic placeholder tokens the BE stamps when it can't resolve a real
// dimension name (e.g. metric_display_names.column -> "Column", or an
// axis label derived from a field literally named "column"). We reject
// these so they never surface as an axis title.
const PLACEHOLDER_AXIS_TITLES = new Set([
  '', 'column', 'col', 'row', 'name', 'value', 'category', 'series', 'index', 'group', 'group_by',
]);

function isMeaningfulTitle(label: unknown): label is string {
  return typeof label === 'string'
    && label.trim() !== ''
    && !PLACEHOLDER_AXIS_TITLES.has(label.trim().toLowerCase());
}

// First candidate that is a real label (not a placeholder), trimmed.
function firstMeaningfulTitle(...candidates: Array<unknown>): string {
  for (const c of candidates) {
    if (isMeaningfulTitle(c)) return c.trim();
  }
  return '';
}

// "Prod Class … and Prod Class …" (self-matrix where both axes share a
// dimension) collapses to a single label; otherwise returned as-is.
function dedupeConjoinedLabel(label: string): string {
  const parts = label.split(/\s+and\s+/i).map((p) => p.trim()).filter(Boolean);
  if (parts.length === 2 && parts[0].toLowerCase() === parts[1].toLowerCase()) {
    return parts[0];
  }
  return label;
}

/**
 * Resolve the row/col dimension display names used as axis titles.
 * Sourcing priority (most reliable first):
 *   1. lineage_v2.dimensions — the canonical dimension labels the
 *      lineage panel renders. For matrix/heatmap the BE emits the col
 *      dim as slot 'x_axis' and the row dim as slot 'y_axis'; a
 *      self-matrix (same dim on both axes) emits only the 'x_axis' one.
 *   2. metric_display_names aliases the matrix builder stamps.
 *   3. Generic x/y axis labels, then the raw field key.
 * Placeholder tokens ("Column", "name", …) are skipped at every step.
 * Self-matrices (one shared dimension) reuse the single label on both
 * axes and collapse any "X and X" conjunction.
 */
function resolveAxisTitles(
  chart: ChartComponentProps['chart'],
  fallbackXKey?: string,
  fallbackYKey?: string,
): { xAxisLabel: string; yAxisLabel: string } {
  const displayNames = chart.metric_display_names || {};
  const dims = chart.lineage_v2?.dimensions || [];
  const xDimLabel = dims.find((d) => d.slot === 'x_axis')?.label;
  const yDimLabel = dims.find((d) => d.slot === 'y_axis' || d.slot === 'group_by')?.label;
  const colDim = (chart as any).matrix_col_dim as string | undefined;
  const rowDim = (chart as any).matrix_row_dim as string | undefined;
  const xField = (chart as any).x_field as string | undefined;

  let xAxisLabel = firstMeaningfulTitle(
    xDimLabel,
    displayNames.column, displayNames.Column,
    colDim ? displayNames[colDim] : undefined,
    xField ? displayNames[xField] : undefined,
    chart.x_axis_label,
    fallbackXKey,
  );
  let yAxisLabel = firstMeaningfulTitle(
    yDimLabel,
    displayNames.name, displayNames.Name,
    rowDim ? displayNames[rowDim] : undefined,
    fallbackYKey,
  );

  // Self-matrix: one axis resolved, the other didn't — reuse the label.
  if (!xAxisLabel && yAxisLabel) xAxisLabel = yAxisLabel;
  if (!yAxisLabel && xAxisLabel) yAxisLabel = xAxisLabel;

  return {
    xAxisLabel: dedupeConjoinedLabel(xAxisLabel),
    yAxisLabel: dedupeConjoinedLabel(yAxisLabel),
  };
}

/**
 * Build the heatmap series from chart.cells[] when present (BE
 * canonical contract — populated by chart_data_service._build_matrix
 * for matrix / heatmap / sensitivity_matrix / correlation_matrix).
 *
 * Each cell carries {row, col, value, secondary_value?, tone?, raw}.
 * We use ``value`` for the visualMap range (color binning); raw is
 * preserved for tooltips so users can see the secondary measure when
 * matrix_mode == primary_value_secondary_color.
 */
function modelFromCells(chart: ChartComponentProps['chart']): HeatModel | null {
  const cells = chart.cells || [];
  if (cells.length === 0) return null;

  const xCategories: string[] = [];
  const yCategories: string[] = [];
  const xSeen = new Set<string>();
  const ySeen = new Set<string>();
  cells.forEach((cell) => {
    const x = String(cell.col ?? '');
    const y = String(cell.row ?? '');
    if (!xSeen.has(x)) { xSeen.add(x); xCategories.push(x); }
    if (!ySeen.has(y)) { ySeen.add(y); yCategories.push(y); }
  });

  const points: HeatPoint[] = [];
  let min = Infinity;
  let max = -Infinity;
  cells.forEach((cell) => {
    const xIdx = xCategories.indexOf(String(cell.col ?? ''));
    const yIdx = yCategories.indexOf(String(cell.row ?? ''));
    const v = typeof cell.value === 'number' && Number.isFinite(cell.value) ? cell.value : null;
    if (v == null || xIdx < 0 || yIdx < 0) return;
    if (v < min) min = v;
    if (v > max) max = v;
    points.push({ xIdx, yIdx, value: v, raw: (cell as any).raw ?? null });
  });

  if (points.length === 0) return null;
  if (min === max) {
    // Avoid a degenerate visualMap range that paints every cell the same.
    min = min - 1;
    max = max + 1;
  }

  // Surface measure labels from metric_display_names when available so
  // tooltips read "Net Sales (USD): $1.2M" instead of raw column keys.
  const displayNames = chart.metric_display_names || {};
  const primaryLabel = displayNames.value || displayNames.Value || displayNames.primary || chart.y_axis_label || 'Value';
  const { xAxisLabel, yAxisLabel } = resolveAxisTitles(chart);

  return {
    xCategories,
    yCategories,
    points,
    min,
    max,
    primaryLabel,
    xAxisLabel,
    yAxisLabel,
  };
}

/**
 * Legacy fallback — heuristic detection of x/y/value keys from
 * chart.data[]. Used when chart.cells is absent (older fixtures or
 * non-orchestrator paths).
 */
function modelFromData(chart: ChartComponentProps['chart']): HeatModel | null {
  const data = chart.data || [];
  if (data.length === 0) return null;

  const keys = Array.from(new Set(data.flatMap((row) => Object.keys(row))));
  const categoricalKeys = keys.filter((key) => data.some((row) => !isNumericLike(row[key]) && row[key] != null && row[key] !== ''));
  const numericKeys = keys.filter((key) => data.some((row) => isNumericLike(row[key])));
  const xKey = categoricalKeys[0] || keys[0];
  const yKey = categoricalKeys.find((key) => key !== xKey) || categoricalKeys[1] || keys[1] || xKey;
  const valKey = numericKeys.find((key) => key !== xKey && key !== yKey) || numericKeys[0] || keys[2];

  if (!xKey || !yKey || !valKey) return null;

  const xCategories: string[] = [];
  const yCategories: string[] = [];
  const xSeen = new Set<string>();
  const ySeen = new Set<string>();
  data.forEach((row) => {
    const x = String(row[xKey] ?? '');
    const y = String(row[yKey] ?? '');
    if (!xSeen.has(x)) { xSeen.add(x); xCategories.push(x); }
    if (!ySeen.has(y)) { ySeen.add(y); yCategories.push(y); }
  });

  const points: HeatPoint[] = [];
  let min = Infinity;
  let max = -Infinity;
  data.forEach((row) => {
    const xIdx = xCategories.indexOf(String(row[xKey] ?? ''));
    const yIdx = yCategories.indexOf(String(row[yKey] ?? ''));
    const v = toNumericValue(row[valKey]);
    if (typeof v !== 'number' || !Number.isFinite(v)) return;
    if (xIdx < 0 || yIdx < 0) return;
    if (v < min) min = v;
    if (v > max) max = v;
    points.push({ xIdx, yIdx, value: v, raw: row });
  });

  if (points.length === 0) return null;
  if (min === max) { min = min - 1; max = max + 1; }

  const displayNames = chart.metric_display_names || {};
  const primaryLabel = displayNames[valKey] || chart.y_axis_label || valKey;
  const { xAxisLabel, yAxisLabel } = resolveAxisTitles(chart, displayNames[xKey] || xKey, displayNames[yKey] || yKey);

  return { xCategories, yCategories, points, min, max, primaryLabel, xAxisLabel, yAxisLabel };
}

export const HeatmapChart: React.FC<ChartComponentProps> = ({ chart, height = 320, colors }) => {
  const model = useMemo<HeatModel | null>(() => {
    return modelFromCells(chart) || modelFromData(chart);
  }, [chart]);

  const option = useMemo((): EChartsOption => {
    if (!model) return {};
    const base = getBaseOption(colors);
    const palette = colors && colors.length >= 2 ? colors : FALLBACK_HEAT;

    const heatData = model.points.map((p) => [p.xIdx, p.yIdx, p.value]);

    return {
      ...base,
      grid: { top: 40, right: 80, bottom: 56, left: CHART_GRID_LEFT, containLabel: true },
      xAxis: {
        type: 'category',
        data: model.xCategories,
        name: model.xAxisLabel || undefined,
        nameLocation: 'middle' as const,
        // Sit the axis title below the (rotated) category labels.
        nameGap: 64,
        // overflow:'truncate' caps the title length so a long dimension
        // name (e.g. self-matrices) gets an ellipsis instead of running
        // the full axis width.
        nameTextStyle: { fontSize: 11, fontWeight: 600 as const, color: '#5C4D54', overflow: 'truncate' as const, ellipsis: '…', width: 260 },
        // interval: 0 forces every column label to render — ECharts'
        // default ('auto') drops overlapping labels, which left blank
        // columns. rotate keeps long labels from colliding.
        axisLabel: {
          fontSize: 11,
          color: '#7B6B72',
          interval: 0,
          rotate: 45,
          hideOverlap: false,
          formatter: (value: string) => (value && value.length > 16 ? `${value.slice(0, 15)}…` : value),
        },
        splitArea: { show: true },
      },
      yAxis: {
        type: 'category',
        data: model.yCategories,
        name: model.yAxisLabel || undefined,
        nameLocation: 'middle' as const,
        nameGap: 130,
        // width caps the rotated title to the plot height so long
        // dimension names get an ellipsis rather than overflowing.
        nameTextStyle: { fontSize: 11, fontWeight: 600 as const, color: '#5C4D54', overflow: 'truncate' as const, ellipsis: '…', width: Math.max(120, height - 80) },
        axisLabel: {
          fontSize: 11,
          color: '#7B6B72',
          formatter: (value: string) => (value && value.length > 22 ? `${value.slice(0, 21)}…` : value),
        },
        splitArea: { show: true },
      },
      visualMap: {
        min: model.min,
        max: model.max,
        calculable: true,
        orient: 'vertical' as const,
        right: 0,
        top: 'center' as const,
        inRange: { color: [palette[0], palette[Math.floor(palette.length / 2)], palette[palette.length - 1]] },
        textStyle: { color: '#7B6B72', fontSize: 11 },
      },
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'item',
        formatter: (params: unknown) => {
          const p = params as { value?: number[] };
          const xIdx = p.value?.[0] ?? 0;
          const yIdx = p.value?.[1] ?? 0;
          const v = p.value?.[2];
          const x = model.xCategories[xIdx] ?? '';
          const y = model.yCategories[yIdx] ?? '';
          // Surface secondary measure from cell.raw when matrix_mode
          // is primary_value_secondary_color — read by hovering on the
          // colour-coded cell. We don't know the exact key name, so
          // pick the first non-row/non-col numeric field on raw.
          const point = model.points.find((q) => q.xIdx === xIdx && q.yIdx === yIdx);
          const rawNumericLines: string[] = [];
          const raw = point?.raw;
          if (raw && typeof raw === 'object') {
            Object.entries(raw).forEach(([k, val]) => {
              if (typeof val === 'number' && Number.isFinite(val) && val !== v) {
                rawNumericLines.push(`<div style="display:flex;justify-content:space-between;gap:12px"><span style="color:#D9CFC4">${k}</span><span>${formatTooltipValue(val)}</span></div>`);
              }
            });
          }
          const secondaryHtml = rawNumericLines.length > 0
            ? `<div style="margin-top:4px;padding-top:4px;border-top:1px solid rgba(247,243,238,.18);font-size:11px">${rawNumericLines.slice(0, 3).join('')}</div>`
            : '';
          return `<div style="font-size:12px"><b>${y}</b> / <b>${x}</b><br/>${model.primaryLabel}: ${formatTooltipValue(v)}${secondaryHtml}</div>`;
        },
      },
      series: [{
        type: 'heatmap',
        data: heatData,
        label: { show: heatData.length <= 50, fontSize: 10, formatter: (params: any) => formatNumberShort(params?.value?.[2]) },
        emphasis: { itemStyle: { borderColor: '#3F3338', borderWidth: 1 } },
      }],
    };
  }, [model, colors]);

  if (!model) {
    return (
      <div className="flex items-center justify-center rounded-[10px] border border-[#EDE6E1] bg-[#FAF7F1] text-sm text-[#7B6B72]" style={{ height }}>
        No matrix cells supplied.
      </div>
    );
  }

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
